import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { Loader } from 'Common/Loader/Loader'
import { SegmentedControl } from 'Common/Components/SegmentedControl'
import { SettingsPage } from 'Features/Projects/Scenarios/EditSettings/SettingsPage'
import { EditSettingsDataFilter } from '../../EditSettings.DataFilter'
import { useSettingsContext } from '../../EditSettings.types'
import { useBatchSettings, useProductSettings } from '../LineSettings.data'
import { BatchSetting, ProductSetting } from '../LineSettings.types'
import { TotalProductionCycles } from './TotalProduction.Cycles'
import { TotalProductionTable } from './TotalProduction.Table'
import { toTotalRows } from './totalRows'

type Layout = 'cycles' | 'table'

const LAYOUTS: { value: Layout; label: string }[] = [
  { value: 'cycles', label: 'Cycles' },
  { value: 'table', label: 'Table' },
]

/**
 * Batch production and SKU production together, each batch group with its
 * SKU groups under it. Two layouts: Cycles (Scheduler-style rows with each
 * cycle range drawn on a 1 / 2 / 4 / 8-week track) and Table (a grid with
 * every input). Reads the same two queries as those pages, so a save on any
 * of them shows on all.
 */
export const TotalProductionView = () => {
  const { projectId, scenarioId } = useParams() as {
    projectId: string
    scenarioId: string
  }
  const { state } = useSettingsContext()
  const { selectedBaseSourceSite, selectedWorkCentre } = state
  const [layout, setLayout] = useState<Layout>('cycles')

  const { data: products, isLoading: productsLoading } = useProductSettings({
    projectId,
    scenarioId,
  })
  const { data: batches, isLoading: batchesLoading } = useBatchSettings({
    projectId,
    scenarioId,
  })

  // Every row (the cycle view keeps its edits across filter changes), then
  // those on the selected site and line.
  const allRows = useMemo(
    () =>
      toTotalRows(
        (batches ?? []) as BatchSetting[],
        (products ?? []) as ProductSetting[]
      ),
    [batches, products]
  )
  const rows = useMemo(
    () =>
      allRows.filter(
        (row) =>
          (!selectedBaseSourceSite ||
            selectedBaseSourceSite === 'All' ||
            row.baseSourceSiteCode === selectedBaseSourceSite) &&
          (!selectedWorkCentre ||
            selectedWorkCentre === 'All' ||
            row.workCentreCode === selectedWorkCentre)
      ),
    [allRows, selectedBaseSourceSite, selectedWorkCentre]
  )

  const isLoading = productsLoading || batchesLoading

  return (
    <SettingsPage
      title="Total production"
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SegmentedControl
            label="Layout"
            value={layout}
            options={LAYOUTS}
            onChange={setLayout}
          />
          <EditSettingsDataFilter />
        </Box>
      }
    >
      {isLoading ? (
        <Loader />
      ) : layout === 'cycles' ? (
        <TotalProductionCycles rows={rows} allRows={allRows} />
      ) : (
        <TotalProductionTable data={rows} isLoading={isLoading} />
      )}
    </SettingsPage>
  )
}
